/**
 * Assign Account Manager Dialog Component
 * Displays a confirmation dialog for bulk assigning account managers to customers
 * Shows counts of new assignments vs. updates to existing assignments
 */

import { AlertCircle, CheckCircle2, UserCheck, Users } from 'lucide-react';
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import type { RiskLevelCode } from '@/lib/risk-level';

interface Customer {
  id: string;
  name: string;
  accountNumber: string;
  currency: 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD';
  outstanding: number;
  overdue: number;
  creditLimit: number;
  dso: number;
  risk: RiskLevelCode;
  lastPayment: string;
  contact: string;
  phone: string;
  address: string;
  paymentTerms: string;
  collectionStrategyId: string;
  creditControllerId?: string;
  accountManagerId?: string;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

interface AssignAccountManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCustomers: Customer[];
  users: User[];
  onConfirm: (userId: string) => void;
}

export function AssignAccountManagerDialog({
  open,
  onOpenChange,
  selectedCustomers,
  users,
  onConfirm,
}: AssignAccountManagerDialogProps) {
  const [selectedUserId, setSelectedUserId] = React.useState<string>('');

  // Preselect account manager when a single customer is selected and has one assigned
  React.useEffect(() => {
    if (open && selectedCustomers.length === 1) {
      const managerId = selectedCustomers[0].accountManagerId;
      if (managerId && users.some((u) => u.id === managerId)) {
        setSelectedUserId(managerId);
      } else {
        setSelectedUserId('');
      }
    } else if (open) {
      setSelectedUserId('');
    }
  }, [open, selectedCustomers, users]);

  // Calculate counts
  const totalSelected = selectedCustomers.length;
  const customersWithNoManager = selectedCustomers.filter(
    (c) => !c.accountManagerId || c.accountManagerId === '',
  ).length;
  const customersWithExistingManager = totalSelected - customersWithNoManager;

  // Get selected user details
  const selectedUser = users.find((u) => u.id === selectedUserId);

  const handleConfirm = () => {
    if (selectedUserId) {
      onConfirm(selectedUserId);
      setSelectedUserId('');
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    setSelectedUserId('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <UserCheck className="h-5 w-5 text-primary" />
            Assign Account Manager
          </DialogTitle>
          <DialogDescription>Select an account manager to assign to the selected customer accounts.</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* User Selection */}
          <div className="space-y-2">
            <Label htmlFor="user-select">Account Manager</Label>
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger id="user-select">
                <SelectValue placeholder="Select an account manager..." />
              </SelectTrigger>
              <SelectContent>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {user.firstName} {user.lastName}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {user.role}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedUser && <p className="text-sm text-muted-foreground mt-2">{selectedUser.email}</p>}
          </div>

          <Separator />

          {/* Impact Summary */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
              <h4 className="font-semibold text-sm">Impact Summary</h4>
            </div>

            <div className="grid gap-3">
              {/* Total Selected */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Total Selected</span>
                </div>
                <Badge variant="secondary" className="text-base font-semibold">
                  {totalSelected}
                </Badge>
              </div>

              {/* New Assignments */}
              {customersWithNoManager > 0 && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-500" />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-green-900 dark:text-green-100">
                        New Manager Assignments
                      </span>
                      <span className="text-xs text-green-700 dark:text-green-400">
                        Customers with no existing account manager
                      </span>
                    </div>
                  </div>
                  <Badge className="bg-green-600 hover:bg-green-700 text-white text-base font-semibold">
                    {customersWithNoManager}
                  </Badge>
                </div>
              )}

              {/* Manager Updates */}
              {customersWithExistingManager > 0 && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-500" />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-amber-900 dark:text-amber-100">Manager Updates</span>
                      <span className="text-xs text-amber-700 dark:text-amber-400">
                        Customers with existing account manager will be updated
                      </span>
                    </div>
                  </div>
                  <Badge className="bg-amber-600 hover:bg-amber-700 text-white text-base font-semibold">
                    {customersWithExistingManager}
                  </Badge>
                </div>
              )}
            </div>

            {/* Information Note */}
            <div className="flex gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900">
              <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-900 dark:text-blue-100">
                <strong>Note:</strong> This action will immediately assign the selected account manager to all{' '}
                {totalSelected} customer{totalSelected !== 1 ? 's' : ''}.
                {customersWithExistingManager > 0 && (
                  <> Existing account manager assignments will be replaced with the new assignment.</>
                )}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!selectedUserId} className="gap-2">
            <CheckCircle2 className="h-4 w-4" />
            Confirm Assignment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
